import React from 'react';
import { Link } from 'react-router-dom';
import { Wallet, ShieldCheck, Zap, Globe2 } from 'lucide-react';
import ContainerPageLayout, { CodeBlock } from '../components/ContainerPageLayout';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'prerequisites', label: 'Prerequisites' },
  { id: 'integration-steps', label: 'Integration Steps', children: [
    { id: 'step-frontend', label: '1. Frontend — Google Pay Button' },
    { id: 'step-token', label: '2. Receive Google Pay Token' },
    { id: 'step-backend', label: '3. Backend — Submit to eCOMM' },
  ]},
  { id: 'request', label: 'Request Payload' },
  { id: 'response', label: 'Response' },
  { id: 'security', label: 'Security & Compliance' },
  { id: 'testing', label: 'Testing' },
];

const FRONTEND_SAMPLE = `<!-- Google Pay button placeholder -->
<div id="googlepay-button-container"></div>

<script src="https://pay.google.com/gp/p/js/pay.js"></script>
<script>
  const paymentsClient = new google.payments.api.PaymentsClient({
    environment: 'TEST' // change to 'PRODUCTION' when live
  });

  const paymentDataRequest = {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [{
      type: 'CARD',
      parameters: {
        allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
        allowedCardNetworks: ['VISA', 'MASTERCARD']
      },
      tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY',
        parameters: {
          gateway: 'ecomm365',
          gatewayMerchantId: 'YOUR_ECOMM_MERCHANT_ID'
        }
      }
    }],
    transactionInfo: {
      totalPriceStatus: 'FINAL',
      totalPrice: '10.00',
      currencyCode: 'EUR',
      countryCode: 'IE'
    },
    merchantInfo: {
      merchantId: 'YOUR_GOOGLE_MERCHANT_ID',
      merchantName: 'Your Business Name'
    }
  };

  const button = paymentsClient.createButton({
    onClick: () => {
      paymentsClient.loadPaymentData(paymentDataRequest)
        .then(paymentData => sendTokenToServer(paymentData))
        .catch(err => console.error(err));
    }
  });
  document.getElementById('googlepay-button-container').appendChild(button);
</script>`;

const BACKEND_SAMPLE = `[HttpPost]
public async Task<IActionResult> ProcessGooglePay([FromBody] GooglePayRequest req)
{
    var client = new HttpClient();
    var payload = new
    {
        username     = "YourUsername",
        password     = "YourPassword",
        messageId    = Guid.NewGuid().ToString(),
        apiSignature = "GooglePay",
        data         = "<R>" +
                          "<R1>EUR</R1>" +
                          "<R2>1000596</R2>" +
                          "<R3>1000</R3>" +
                          $"<R4>{req.GooglePayToken}</R4>" +
                          "<R5>order-12345</R5>" +
                       "</R>"
    };

    var content = new FormUrlEncodedContent(payload
        .GetType()
        .GetProperties()
        .ToDictionary(p => p.Name, p => p.GetValue(payload)?.ToString()));

    var response = await client.PostAsync(
        "https://staging.ecomm365.com/acqapi/service.ashx",
        content);

    return Content(await response.Content.ReadAsStringAsync(), "application/xml");
}`;

const REQUEST_XML = `<!-- Google Pay Payment Request -->
<R>
    <R1>EUR</R1>
    <R2>1000596</R2>
    <R3>1000</R3>
    <R4>{GOOGLE_PAY_TOKEN}</R4>
    <R5>order-12345</R5>
</R>`;

const RESPONSE_SUCCESS = `<!-- Sample Successful Response -->
<?xml version="1.0"?>
<R>
    <R1>0000</R1>
    <R2>Transaction Approved</R2>
    <R3>e7c1b9d2-3b21-4a8e-9b81-7fa6b9d2b3a1</R3>
    <R4>2026-02-04T11:08:42.001Z</R4>
    <R5>1000</R5>
    <R6>978</R6>
</R>`;

const RESPONSE_FAIL = `<!-- Sample Declined Response -->
<?xml version="1.0"?>
<R>
    <R1>0005</R1>
    <R2>Do Not Honour</R2>
    <R3>4af7c1e0-b32f-4e2a-9c81-d49bda3e8a17</R3>
    <R4>2026-02-04T11:09:14.012Z</R4>
</R>`;

const FORM_FIELDS = [
  { name: 'R1', desc: 'Alphabetic Currency Code (e.g. EUR, GBP).',                       def: 'AN(3)' },
  { name: 'R2', desc: 'Vendor ID issued by eCOMM365.',                                    def: 'N(10)' },
  { name: 'R3', desc: 'Transaction amount in minor units (e.g. €10.00 → 1000).',          def: 'N(12)' },
  { name: 'R4', desc: 'Google Pay payment token received from Google Pay API.',           def: 'AN(2000)' },
  { name: 'R5', desc: 'Merchant transaction reference / order ID.',                        def: 'AN(50)' },
];

const GooglePay = () => (
  <ContainerPageLayout
    currentId="google-pay"
    title="Google Pay"
    subtitle={
      <>
        Accept <strong>Google Pay</strong> payments through eCOMM. A server-to-server integration that processes Google Pay tokens for fast, secure, frictionless checkouts.
      </>
    }
    titleBadge="GooglePay"
    signature="googlepay"
    toc={TOC}
    mainTestId="google-pay-page"
    sidebarTestId="google-pay-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Overview</h2>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-gray-100 text-gray-700 border border-gray-200 uppercase tracking-wide" data-testid="googlepay-meaning-chip">
          <Wallet size={12} />
          Google Pay Integration
        </span>
      </div>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The <strong>Google Pay API</strong> lets your customers pay using cards saved to their Google Account. Your checkout receives a tokenised payment object from Google which you then submit to eCOMM for authorisation — no raw card data ever touches your servers.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This integration works alongside your existing eCOMM{' '}
        <Link to="/technical-documentation/api-reference" className="text-[#1F4E79] underline hover:text-[#163a5f]">Direct API</Link>{' '}
        setup. Once your merchant account is enabled for Google Pay, you only need to add the frontend button and a small backend handler to forward the token.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <Zap size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">Frictionless Checkout</p>
            <p className="text-xs text-gray-600 mt-1">One-tap pay with biometrics — reduces cart abandonment and speeds up conversion.</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <ShieldCheck size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">No Card Data On Your Servers</p>
            <p className="text-xs text-gray-600 mt-1">Google supplies a tokenised payment object — your PCI exposure is dramatically reduced.</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <Globe2 size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">Global Coverage</p>
            <p className="text-xs text-gray-600 mt-1">Supports Visa and Mastercard payment networks in every Google Pay supported region.</p>
          </div>
        </div>
      </div>
    </section>

    {/* How It Works */}
    <section id="how-it-works" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">How It Works</h2>
      <ol className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="googlepay-flow">
        {[
          { n: 1, t: 'Customer Taps Google Pay', d: 'Your checkout displays the Google Pay button. The customer authorises payment with biometrics or their PIN.' },
          { n: 2, t: 'Google Returns A Token', d: 'Google supplies a tokenised payment object to your frontend. The raw PAN is never exposed to your system.' },
          { n: 3, t: 'Submit Token To eCOMM', d: 'Your backend forwards the Google Pay token to the eCOMM Google Pay endpoint for authorisation. Funds clear to your settlement account.' },
        ].map((s, i, arr) => (
          <li key={s.n} className="relative rounded-lg border-2 border-[#1F4E79]/15 bg-gradient-to-br from-white to-blue-50/40 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 bg-[#1F4E79] text-white rounded-full text-xs font-bold">{s.n}</span>
              <h4 className="text-sm font-bold text-gray-900">{s.t}</h4>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">{s.d}</p>
            {i < arr.length - 1 && (
              <span className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 text-[#1F4E79] text-xl font-bold select-none">&rsaquo;</span>
            )}
          </li>
        ))}
      </ol>
    </section>

    {/* Prerequisites */}
    <section id="prerequisites" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Prerequisites</h2>
      <ul className="space-y-2 text-gray-700 text-base leading-relaxed list-disc pl-5">
        <li>Active eCOMM merchant account with <strong>Google Pay enabled</strong> (contact eCOMM365 to opt in).</li>
        <li>Registered Google Pay <strong>Merchant ID</strong> via the Google Pay Business Console.</li>
        <li>HTTPS hosted checkout page (Google Pay only loads on secure origins).</li>
        <li>Backend able to make server-to-server requests to <code>service.ashx</code>.</li>
      </ul>
    </section>

    {/* Integration Steps */}
    <section id="integration-steps" className="scroll-mt-28">
      <div id="step-frontend" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">1. Frontend — Render The Google Pay Button</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Load Google's pay.js client, configure the payment data request with your Google merchant ID and your eCOMM merchant ID, then mount the button into your checkout.
        </p>
        <CodeBlock language="html" code={FRONTEND_SAMPLE} />
      </div>

      <div id="step-token" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">2. Receive The Google Pay Token</h3>
        <p className="text-gray-700 leading-relaxed">
          When the customer authorises payment, <code>loadPaymentData</code> resolves with a <code>paymentData</code> object containing the encrypted Google Pay token. Send this token to your backend over HTTPS.
        </p>
      </div>

      <div id="step-backend" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">3. Backend — Submit To The eCOMM Google Pay Endpoint</h3>
        <p className="text-gray-700 leading-relaxed mb-4">
          Send a server-to-server POST to <code>service.ashx</code> with <code>apiSignature=GooglePay</code> and the Google Pay token inside the request body.
        </p>
        <CodeBlock language="csharp" code={BACKEND_SAMPLE} />
      </div>
    </section>

    {/* Request payload */}
    <section id="request" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Request Payload</h2>
      <p className="text-gray-700 leading-relaxed mb-4">
        The <code>Data</code> parameter accepts a simple XML body describing the transaction.
      </p>
      <CodeBlock language="xml" code={REQUEST_XML} />
      <div className="mt-6 overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left font-semibold">Field</th>
              <th className="px-4 py-2 text-left font-semibold">Description</th>
              <th className="px-4 py-2 text-left font-semibold">Definition</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {FORM_FIELDS.map((f) => (
              <tr key={f.name} className="bg-white">
                <td className="px-4 py-2 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
                <td className="px-4 py-2 text-gray-700">{f.desc}</td>
                <td className="px-4 py-2 font-mono text-xs text-gray-600">{f.def}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>

    {/* Response */}
    <section id="response" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Response</h2>
      <p className="text-gray-700 leading-relaxed mb-3"><strong>Success</strong> — response code <code>0000</code>:</p>
      <CodeBlock language="xml" code={RESPONSE_SUCCESS} />
      <p className="text-gray-700 leading-relaxed mt-6 mb-3"><strong>Declined / Failure</strong> — non-zero response code:</p>
      <CodeBlock language="xml" code={RESPONSE_FAIL} />
    </section>

    {/* Security */}
    <section id="security" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Security &amp; Compliance</h2>
      <ul className="space-y-2 text-gray-700 text-base leading-relaxed list-disc pl-5">
        <li>The Google Pay token is encrypted by Google — eCOMM decrypts it server-side.</li>
        <li>Tokens are short-lived and single-use; replays are rejected.</li>
        <li>Your servers never see raw PAN data, significantly reducing PCI DSS scope.</li>
        <li>All eCOMM endpoints are TLS 1.2+ only.</li>
      </ul>
    </section>

    {/* Testing */}
    <section id="testing" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-2">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Testing</h2>
      <p className="text-gray-700 leading-relaxed mb-3">
        Use the Google Pay <code>TEST</code> environment in your frontend during integration. Successful test transactions appear in your eCOMM staging dashboard.
      </p>
      <p className="text-gray-700 leading-relaxed">
        See the{' '}
        <Link to="/technical-documentation/appendix/test-cards" className="text-[#1F4E79] underline hover:text-[#163a5f]">Test Cards appendix</Link>{' '}
        for sample card numbers that work with Google Pay sandbox tokens.
      </p>
    </section>
  </ContainerPageLayout>
);

export default GooglePay;
